import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard,

  Hammer,
  FileText,
  Receipt,
  Settings,
  LogOut,
  Mic,
  BarChart3,
  Kanban,
  Inbox,
  Package,
  UserSquare2,
  CheckSquare,
  UserCog,
  Sun,
  Moon,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { ROLE_LABEL, hasRole } from "@/lib/roles";
import { WerkoWordmark } from "@/components/WerkoWordmark";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/tasks", label: "Tâches", icon: CheckSquare },
  { to: "/contacts", label: "Contacts", icon: UserSquare2 },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, hiddenForRoles: ["gestionnaire_chantier"] },
  { to: "/poc", label: "Devis dicté", icon: Mic, hiddenForRoles: ["secretaire"] },
  { to: "/quotes", label: "Devis", icon: FileText },
  { to: "/chantiers", label: "Chantiers", icon: Hammer },
  { to: "/factures", label: "Factures", icon: Receipt },
  { to: "/supplier-invoices", label: "Factures fournisseurs", icon: Inbox, hiddenForRoles: ["commercial"] },
  // Phase B lot correctifs (2026-09-10) — Catalogue accessible aussi au
  // gestionnaire_chantier (backend autorise POST/PATCH). Masqué au secrétaire
  // (pas de besoin métier — le back interdit DELETE seulement pour non-admin).
  { to: "/catalog", label: "Catalogue", icon: Package, hiddenForRoles: ["secretaire"] },
  // Phase B — séparateur visuel avant Rapports/Paramètres
  { separator: true },
  { to: "/rapports-comptables", label: "Rapports", icon: BarChart3, hiddenForRoles: ["secretaire", "commercial", "gestionnaire_chantier"] },
  // Phase Perf-1 (13/09/2026) — Vue Admin performance commerciale. Admin only.
  { to: "/performance", label: "Performance", icon: TrendingUp, adminOnly: true },
  { to: "/settings/company", label: "Paramètres", icon: Settings, adminOnly: true },
];

export default function BackofficeLayout() {
  const { state, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const nav = useNavigate();
  const loc = useLocation();
  const [completeness, setCompleteness] = useState(null);
  // Phase 2C.M v3 — Statut Google Calendar affiché dans le dropdown.
  // Fetch lazy à l'ouverture du menu (pas de polling).
  const [gcalStatus, setGcalStatus] = useState(null);
  // Phase B2.1 → D2 — Badge sidebar Tâches = TOTAL pending + RDV à débriefer.
  // Règle produit : "un chiffre affiché doit correspondre à ce que l'utilisateur
  // voit en cliquant". La page /tasks affiche 2 sections : RDV à débriefer +
  // tâches pending → badge = somme des deux.
  const [pendingTasks, setPendingTasks] = useState(0);
  const [rdvToDebriefCount, setRdvToDebriefCount] = useState(0);
  // Phase γ-1 (12/09/2026) — Compteur sidebar "Factures à envoyer" + garde-fou J+3.
  // RBAC : admin / secretaire / commercial → compteur visible (backend filtre
  // par assigned_to pour commercial). gestionnaire_chantier → endpoint 403,
  // le state reste à null → badge non rendu.
  const [invoicesToSend, setInvoicesToSend] = useState(null);

  useEffect(() => {
    if (!state || state === false) return;
    // Compteur Tâches : admin only (existant)
    // Compteur Factures : admin + secretaire + commercial (γ-1)
    const canSeeTasksBadge = hasRole(state.user, ["admin"]);
    const canSeeInvoicesBadge = hasRole(state.user, ["admin", "secretaire", "commercial"]);
    if (!canSeeTasksBadge && !canSeeInvoicesBadge) return;
    let alive = true;
    const load = () => {
      if (canSeeTasksBadge) {
        api.get("/company/completeness")
          .then((r) => alive && setCompleteness(r.data))
          .catch(() => alive && setCompleteness(null));
        api.get("/tasks", { params: { status: "pending" } })
          .then((r) => alive && setPendingTasks(Array.isArray(r.data) ? r.data.length : 0))
          .catch(() => alive && setPendingTasks(0));
        api.get("/rendezvous", { params: { needs_debrief: true } })
          .then((r) => alive && setRdvToDebriefCount(Array.isArray(r.data) ? r.data.length : 0))
          .catch(() => alive && setRdvToDebriefCount(0));
      }
      if (canSeeInvoicesBadge) {
        api.get("/dashboard/invoices-to-send-count")
          .then((r) => alive && setInvoicesToSend(r.data || null))
          .catch(() => alive && setInvoicesToSend(null));
      }
    };
    load();
    return () => { alive = false; };
  }, [state, loc.pathname]);

  const tasksBadgeCount = pendingTasks + rdvToDebriefCount;
  const invoicesToSendCount = invoicesToSend?.count || 0;
  const invoicesToSendJ3 = !!invoicesToSend?.has_overdue_to_send;

  if (!state || state === false) return null;
  const { user, company } = state;

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  return (
    <div className="flex min-h-screen bg-platform-bg text-slate-100">
      {/* Sidebar */}
      <aside
        className="hidden w-60 shrink-0 flex-col border-r border-slate-800 bg-slate-900/50 md:flex"
        data-testid="sidebar"
      >
        <div className="border-b border-slate-800 px-5 py-5">
          <WerkoWordmark size="sm" />
          <p className="mt-2 truncate text-xs text-slate-400" data-testid="company-name">
            {company.name}
          </p>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n, idx) => {
            if (n.separator) {
              return <hr key={`sep-${idx}`} className="my-2 border-slate-800" />;
            }
            if (n.adminOnly && !hasRole(user, ["admin"])) return null;
            if (n.hiddenForRoles && n.hiddenForRoles.includes(user.role)) return null;
            const Icon = n.icon;
            const cls = ({ isActive }) => {
              const activeMatch =
                n.to.startsWith("/settings")
                  ? loc.pathname.startsWith("/settings")
                  : isActive;
              return `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all ${
                n.disabled
                  ? "cursor-not-allowed text-slate-500"
                  : activeMatch
                    ? "bg-[#C4553B]/20 text-[#C4553B] font-medium"
                    : "text-slate-300 hover:bg-platform-hover hover:text-platform-heading"
              }`;
            };
            if (n.disabled) {
              return (
                <div key={n.to} className={cls({ isActive: false })} title="Bientôt">
                  <Icon className="h-4 w-4" />
                  {n.label}
                  <span className="ml-auto rounded-full bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                    Bientôt
                  </span>
                </div>
              );
            }
            const hasSettingsBadge = n.to.startsWith("/settings") && completeness && completeness.count_missing > 0;
            const navTo = hasSettingsBadge ? `${n.to}?missing=1` : n.to;
            return (
              <NavLink
                key={n.to}
                to={navTo}
                className={cls}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="h-4 w-4" />
                {n.label}
                {n.to === "/tasks" && tasksBadgeCount > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center rounded-full bg-[#C4553B] px-1.5 py-0.5 text-[10px] font-bold text-white"
                    data-testid="sidebar-tasks-pending-badge"
                    title={
                      rdvToDebriefCount > 0
                        ? `${pendingTasks} tâche(s) en cours + ${rdvToDebriefCount} RDV à débriefer — cliquez pour voir`
                        : `${pendingTasks} tâche(s) en cours — cliquez pour voir la liste`
                    }
                  >
                    {tasksBadgeCount}
                  </span>
                )}
                {n.to === "/factures" && invoicesToSendCount > 0 && (
                  <span
                    className={`ml-auto inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white ${
                      invoicesToSendJ3 ? "bg-[#A63D2F]" : "bg-[#C4553B]"
                    }`}
                    data-testid="sidebar-counter-invoices-to-send"
                    data-severity={invoicesToSendJ3 ? "red" : "normal"}
                    title={
                      invoicesToSendJ3
                        ? `${invoicesToSendCount} facture(s) à envoyer — dont ${invoicesToSend.j3_count} depuis plus de 3 jours`
                        : `${invoicesToSendCount} facture(s) à envoyer — cliquez pour ouvrir la liste`
                    }
                  >
                    {invoicesToSendCount}
                  </span>
                )}
                {hasSettingsBadge && (
                  <span
                    className={`ml-auto inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      completeness.severity === "red"
                        ? "bg-[#A63D2F] text-white"
                        : "bg-[#D99A2B] text-slate-900"
                    }`}
                    data-testid="sidebar-settings-completeness-badge"
                    title={
                      completeness.severity === "red"
                        ? `${completeness.blocking_missing.length} paramètre(s) bloquant(s) — cliquez pour compléter`
                        : `${completeness.count_missing} paramètre(s) à compléter — cliquez pour voir`
                    }
                  >
                    {completeness.count_missing}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 p-3 text-[11px] text-slate-500">
          Wérko · v1
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-800 bg-slate-900/40 px-4 sm:px-6">
          <div className="flex items-center gap-2 md:hidden">
            <WerkoWordmark size="sm" />
            <span className="text-sm text-slate-400">· {company.name}</span>
          </div>
          <div className="hidden text-sm text-slate-400 md:block">
            {ROLE_LABEL[user.role] || user.role}
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu
              onOpenChange={(open) => {
                if (open) {
                  api.get("/integrations/google/status")
                    .then((r) => setGcalStatus(r.data || null))
                    .catch(() => setGcalStatus({ connected: false }));
                }
              }}
            >
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-platform-hover"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#C4553B] text-xs text-white">
                    {initials || "??"}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm sm:inline">{user.first_name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 border-slate-700 bg-slate-900 text-slate-100"
            >
              <DropdownMenuLabel className="text-slate-400">
                {user.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              {/* Toggle thème inline (ne ferme pas le menu — e.preventDefault) */}
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleTheme();
                }}
                data-testid="menu-theme-toggle"
                aria-label={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
              >
                {isDark ? (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    <span className="flex-1">Mode clair</span>
                  </>
                ) : (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    <span className="flex-1">Mode sombre</span>
                  </>
                )}
                <span
                  className={`ml-2 inline-flex h-4 w-7 items-center rounded-full border ${
                    isDark ? "border-slate-700 bg-slate-800" : "border-[#C4553B] bg-[#C4553B]"
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className={`h-3 w-3 rounded-full bg-white transition-transform ${
                      isDark ? "translate-x-0.5" : "translate-x-3.5"
                    }`}
                  />
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => nav("/mon-compte")}
                data-testid="menu-account"
              >
                <UserCog className="mr-2 h-4 w-4" />
                <span className="flex-1">Mon compte</span>
                {gcalStatus && (() => {
                  let color = "bg-slate-500";
                  let label = "Agenda non connecté";
                  let tid = "menu-gcal-status-disconnected";
                  if (gcalStatus.needs_reconnect) {
                    color = "bg-red-500";
                    label = "Reconnexion requise";
                    tid = "menu-gcal-status-reconnect";
                  } else if (gcalStatus.connected && gcalStatus.sync_enabled) {
                    color = "bg-emerald-500";
                    label = "Sync ON";
                    tid = "menu-gcal-status-sync-on";
                  } else if (gcalStatus.connected) {
                    color = "bg-amber-500";
                    label = "Sync en pause";
                    tid = "menu-gcal-status-sync-off";
                  }
                  return (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-[10px] text-slate-400"
                      data-testid={tid}
                      title={label}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
                      {label}
                    </span>
                  );
                })()}
              </DropdownMenuItem>
              {hasRole(user, ["admin"]) && (
                <DropdownMenuItem
                  onSelect={() => nav("/settings/company")}
                  data-testid="menu-settings"
                >
                  <Settings className="mr-2 h-4 w-4" /> Paramètres
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem
                onSelect={async () => {
                  await logout();
                  nav("/login");
                }}
                className="text-red-300 focus:text-red-200"
                data-testid="menu-logout"
              >
                <LogOut className="mr-2 h-4 w-4" /> Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="flex overflow-x-auto border-b border-slate-800 bg-slate-900/40 px-2 md:hidden">
          {NAV.filter((n) => !n.separator && !n.disabled && (!n.adminOnly || hasRole(user, ["admin"])) && !(n.hiddenForRoles && n.hiddenForRoles.includes(user.role))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 px-3 py-2 text-xs ${
                  isActive ? "border-b-2 border-[#C4553B] text-[#C4553B]" : "text-slate-400"
                }`
              }
            >
              <n.icon className="h-3.5 w-3.5" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
